import { ReactNode } from 'react';
import { SxProps } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import { ClearIcon } from '../../../Icons';
import InfoButton from '../../InfoButton';
import Typography from '../../Typography';
import { COLORS } from '../../../theme';

export type SelectFieldOption = { value: any; label: string };

// Controlled select: it does not know about forms, translations or tracking.
// The consumer passes the value and error, and reacts to onChange / onBlur / onClear.
export type SelectFieldProps = {
  options: SelectFieldOption[];
  value?: any;
  onChange?: (value: any) => void;
  onBlur?: () => void;
  // Called by the clear button. Defaults to onChange('').
  onClear?: () => void;
  label?: ReactNode;
  placeholder?: string;
  helperText?: ReactNode;
  error?: ReactNode;
  hideErrorText?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  optional?: boolean;
  // Text shown next to the label when optional. The lib does not translate it.
  optionalLabel?: ReactNode;
  moreInfoTooltip?: ReactNode;
  clearable?: boolean;
  startAdornment?: ReactNode;
  renderOption?: (option: SelectFieldOption) => ReactNode;
  renderValue?: (value: any) => ReactNode;
  sx?: SxProps;
};

function hasValue(value: any) {
  return Array.isArray(value) ? !!value.length : !!value;
}

export default function SelectField({
  options,
  value,
  onChange,
  onBlur,
  onClear,
  label,
  placeholder,
  helperText,
  error,
  hideErrorText,
  disabled = false,
  fullWidth = true,
  optional = false,
  optionalLabel = 'Optional',
  moreInfoTooltip,
  clearable,
  startAdornment,
  renderOption,
  renderValue,
  sx = {},
}: SelectFieldProps) {
  const handleClear = () => (onClear ? onClear() : onChange && onChange(''));

  return (
    <FormControl fullWidth={fullWidth} error={!!error}>
      {label && (
        <FormLabel sx={{ padding: '8px 0', fontWeight: 600, display: 'flex' }}>
          <Typography>{label}</Typography>
          {moreInfoTooltip && <InfoButton title={moreInfoTooltip} />}
          {optional && (
            <Typography sx={{ color: COLORS.gray[1000], marginLeft: 'auto' }}>{optionalLabel}</Typography>
          )}
        </FormLabel>
      )}
      <Select
        color="secondary"
        startAdornment={
          startAdornment ? (
            <InputAdornment position="start" style={{ width: 'auto', color: COLORS.gray[1200] }}>
              {startAdornment}
            </InputAdornment>
          ) : null
        }
        disabled={disabled}
        value={value}
        onChange={(e) => onChange && onChange(e.target.value)}
        onBlur={() => onBlur && onBlur()}
        variant="outlined"
        displayEmpty
        renderValue={(selected: any) =>
          hasValue(selected) ? (
            renderValue ? (
              renderValue(selected)
            ) : (
              options.find((option) => option.value.toString() === selected.toString())?.label
            )
          ) : (
            <Typography style={{ color: COLORS.gray[1000] }}>{placeholder}</Typography>
          )
        }
        sx={{
          background: disabled ? COLORS.gray[300] : COLORS.brand.white,
          paddingRight: '8px',
          '& .MuiSelect-select': {
            paddingY: '12px',
          },
          '& fieldset': {
            borderColor: COLORS.gray[900],
            borderRadius: '8px',
          },
          ...sx,
        }}
        endAdornment={
          clearable && hasValue(value) ? (
            <InputAdornment position="end">
              <IconButton
                aria-label="clear selection"
                onClick={handleClear}
                edge="end"
                size="small"
                sx={{ marginRight: '1em', color: COLORS.gray[1100] }}
              >
                <ClearIcon />
              </IconButton>
            </InputAdornment>
          ) : null
        }
      >
        {options.map((option) =>
          renderOption ? (
            renderOption(option)
          ) : (
            <MenuItem key={option.value} value={option.value} sx={{ padding: '12px' }}>
              {option.label}
            </MenuItem>
          )
        )}
      </Select>
      {((!hideErrorText && error) || helperText) && (
        <FormHelperText sx={{ color: 'inherit', whiteSpace: 'pre-line', fontSize: '14px' }}>
          {(!hideErrorText && error) || helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
}
